import { router, useForm } from '@inertiajs/react'
import AuthLayout from '@/layouts/AuthLayout'
import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'

type LoginStep = 'email' | 'password' | 'otp' | 'sso_unavailable'

interface LoginProps {
  step: LoginStep
  // The email the server resolved from the first step. Null on the initial
  // email step. Never trust it to reveal account existence — it's only echoed
  // back so the user can see what they typed.
  email: string | null
  // Generic, enumeration-safe failure message rendered by the server. Never
  // discloses whether an account exists or is locked.
  error: string | null
  // Seconds remaining before the OTP can be resent. Null when not applicable.
  resend_cooldown_seconds: number | null
}

export default function Login({ step, email, error, resend_cooldown_seconds }: LoginProps) {
  // IC ships a SINGLE login form: email + password together, submitted in one
  // POST to /login. The controller's permanent single-POST path composes email
  // resolution and password verification in that one request. The cascade's
  // stepped states are kept but dark — a password-dialed account (all of them
  // today) never leaves the combined form; only a non-default dial moves a user
  // to the otp code step or the sso_unavailable state. The `email` and
  // `password` steps both render the combined form (initial entry vs a
  // re-render after a failed credential submit, which the server tags
  // `password` with the email preserved).
  const credentials = step === 'email' || step === 'password'

  return (
    <AuthLayout title="Log in to your account">
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          {credentials && <CredentialsStep email={email} error={error} />}
          {step === 'otp' && (
            <OtpStep email={email} error={error} resendCooldownSeconds={resend_cooldown_seconds} />
          )}
          {step === 'sso_unavailable' && <SsoUnavailableStep error={error} />}
        </div>
      </div>
    </AuthLayout>
  )
}

function ErrorAlert({ error }: { error: string | null }) {
  if (!error) return null
  return (
    <div role="alert" className="alert alert-soft alert-error text-sm">
      <span>{error}</span>
    </div>
  )
}

function CredentialsStep({ email, error }: { email: string | null; error: string | null }) {
  // Both fields post together to /login. The server resolves the account from
  // email_address and, for a password-dialed account, verifies the password in
  // the same request; a wrong password re-renders here with the email preserved
  // and the generic (enumeration-safe) error. If the account is dialed to OTP,
  // the server ignores the password and redirects to the code step; if to IdP,
  // to the sso_unavailable state.
  const { data, setData, post, processing } = useForm({ email_address: email ?? '', password: '' })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/login')
  }

  return (
    <>
      <h2 className="card-title text-2xl font-bold mb-1">Log in</h2>
      <p className="text-sm text-base-content/60 mb-3">
        Enter your email and password to continue.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <ErrorAlert error={error} />

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Email</legend>
          <input
            type="email"
            className="input w-full min-h-11"
            value={data.email_address}
            onChange={(e) => setData('email_address', e.target.value)}
            required
            autoFocus={!email}
            autoComplete="email"
          />
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Password</legend>
          <input
            type="password"
            className="input w-full min-h-11"
            value={data.password}
            onChange={(e) => setData('password', e.target.value)}
            required
            autoFocus={Boolean(email)}
            autoComplete="current-password"
          />
          <p className="label whitespace-normal">
            <a href="/forgot_password" className="link link-hover">
              Forgot your password?
            </a>
          </p>
        </fieldset>

        <button type="submit" className="btn btn-primary w-full min-h-11" disabled={processing}>
          {processing && <span className="loading loading-spinner loading-sm" />}
          Log in
        </button>
      </form>

      <p className="text-sm text-center mt-4">
        Don&apos;t have an account?{' '}
        <a href="/signup" className="link link-primary">
          Sign up
        </a>
      </p>
    </>
  )
}

function OtpStep({
  email,
  error,
  resendCooldownSeconds,
}: {
  email: string | null
  error: string | null
  resendCooldownSeconds: number | null
}) {
  const { data, setData, post, processing } = useForm({ code: '' })
  const seed = resendCooldownSeconds ?? 0
  const [remaining, setRemaining] = useState(seed)
  const [prevSeed, setPrevSeed] = useState(seed)
  const [resending, setResending] = useState(false)

  // Reseed the countdown whenever the server hands us a fresh cooldown (e.g.
  // after a successful resend re-renders the page). Adjusting state during
  // render is React's recommended pattern for deriving state from a changed
  // prop — it avoids the extra effect pass.
  if (seed !== prevSeed) {
    setPrevSeed(seed)
    setRemaining(seed)
  }

  // Tick the countdown down to zero, one second at a time.
  useEffect(() => {
    if (remaining <= 0) return
    const timer = setTimeout(() => setRemaining(remaining - 1), 1000)
    return () => clearTimeout(timer)
  }, [remaining])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/login')
  }

  function handleResend() {
    setResending(true)
    router.post(
      '/login/resend',
      {},
      {
        preserveScroll: true,
        onFinish: () => setResending(false),
      }
    )
  }

  const cooldownActive = remaining > 0

  return (
    <>
      <h2 className="card-title text-2xl font-bold mb-1">Enter your sign-in code</h2>
      <p className="text-sm text-base-content/60 mb-3">
        If an account exists for {email ?? 'this address'}, we&apos;ve sent a sign-in code. It
        expires in 15 minutes.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <ErrorAlert error={error} />

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Sign-in code</legend>
          <div className="flex justify-center">
            {/* No w-full: daisyUI positions the digits with a letter-spacing tuned to
                the otp's NATURAL width, so stretching the label desynchronises the
                caret from the cell grid and clips each digit against its right
                border. Centre the component rather than widening it. */}
            <label className="otp otp-lg">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <input
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              pattern="[0-9]{6}"
              value={data.code}
              onChange={(e) => setData('code', e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />
            </label>
          </div>
        </fieldset>

        <button type="submit" className="btn btn-primary w-full min-h-11" disabled={processing}>
          {processing && <span className="loading loading-spinner loading-sm" />}
          Verify
        </button>
      </form>

      <div className="text-sm text-center mt-4">
        {cooldownActive ? (
          <span className="text-base-content/50">Resend code in {remaining}s</span>
        ) : (
          <button
            type="button"
            className="link link-hover min-h-11"
            onClick={handleResend}
            disabled={resending}
          >
            Didn&apos;t get it? Resend code
          </button>
        )}
      </div>

      <p className="text-sm text-center mt-2">
        <a href="/login?change=1" className="link link-hover text-base-content/60">
          Use a different email
        </a>
      </p>
    </>
  )
}

function SsoUnavailableStep({ error }: { error: string | null }) {
  return (
    <>
      <h2 className="card-title text-2xl font-bold mb-1">Single sign-on</h2>

      <div className="space-y-4">
        <ErrorAlert error={error} />

        <div role="alert" className="alert alert-soft alert-info text-sm">
          <span>
            Single sign-on isn&apos;t available for this organization yet. Contact your
            administrator.
          </span>
        </div>

        <p className="text-sm text-center">
          <a href="/login?change=1" className="link link-hover text-base-content/60">
            Use a different email
          </a>
        </p>
      </div>
    </>
  )
}
